/**
 * Toggle Kenya counties from the canonical GTG list; syncs with newline-separated countyText.
 */
import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { GTG_KENYA_COUNTIES, parseKenyaCountiesText } from "@healthdesk/shared-utils";
import { getKenyaCountyGoogleAdsDisplayName } from "../../services/census/getKenyaCountyCensusSnapshot";

const KenyaCountyChecklistPicker = ({ value, onChange, disabled = false }) => {
  const [filter, setFilter] = useState("");
  const filterLower = filter.trim().toLowerCase();

  const selectedSet = useMemo(() => {
    const { counties } = parseKenyaCountiesText(value || "");
    return new Set(counties);
  }, [value]);

  const visibleCounties = useMemo(() => {
    if (!filterLower) return [...GTG_KENYA_COUNTIES];
    return GTG_KENYA_COUNTIES.filter((c) => {
      if (c.toLowerCase().includes(filterLower)) return true;
      return getKenyaCountyGoogleAdsDisplayName(c).toLowerCase().includes(filterLower);
    });
  }, [filterLower]);

  const toggleCounty = useCallback(
    (canonName, checked) => {
      const next = new Set(selectedSet);
      if (checked) next.add(canonName);
      else next.delete(canonName);
      const ordered = GTG_KENYA_COUNTIES.filter((c) => next.has(c));
      onChange(ordered.join("\n"));
    },
    [selectedSet, onChange]
  );

  const selectedCount = selectedSet.size;

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Select counties
      </Typography>
      <TextField
        size="small"
        fullWidth
        label="Filter counties"
        placeholder="Type to narrow the list"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        disabled={disabled}
        sx={{ mb: 1.5 }}
      />
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {selectedCount} selected · same canonical names as the county list above
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          maxHeight: 280,
          overflow: "auto",
          px: 1,
          py: 0.5,
        }}
      >
        <FormGroup>
          {visibleCounties.map((name) => (
            <FormControlLabel
              key={name}
              control={
                <Checkbox
                  size="small"
                  checked={selectedSet.has(name)}
                  onChange={(e) => toggleCounty(name, e.target.checked)}
                  disabled={disabled}
                />
              }
              label={getKenyaCountyGoogleAdsDisplayName(name)}
              sx={{ mr: 0, alignItems: "flex-start", "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
            />
          ))}
        </FormGroup>
        {visibleCounties.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 1 }}>
            No counties match this filter.
          </Typography>
        ) : null}
      </Paper>
    </Box>
  );
};

export default KenyaCountyChecklistPicker;
